import moment from "moment";

import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { PatientConsentModel } from "@/Common/Api/Patient/Dto/PatientConsentModel";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { DateTime } from "@/Common/Helpers/DateTime";
import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";
import { PatientEmailValidator } from "@/Common/Helpers/PatientEmailValidator";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { RepresentantHelper } from "@/Common/Helpers/RepresentantHelper";
import { PatientAppointmentItem } from "@/Common/Models/Appointments/PatientAppointmentItem";
import { PatientAppointmentListItem } from "@/Common/Models/Appointments/PatientAppointmentListItem";
import { Address } from "@/Common/Models/Contacts/Address";
import { Email } from "@/Common/Models/Contacts/Email";
import { Guid } from "@/Common/Models/Guid";
import { PreferredPronouns } from "@/Common/Models/Individuals/Enums/PreferredPronouns";
import { Patient } from "@/Common/Models/Patient/Patient";
import { PatientIdentifier } from "@/Common/Models/Patient/PatientIdentifier";
import { IJPatientCreationModel, IPatientCreationModel } from "@/Common/Models/Patient/UI/IPatientCreationModel";
import { IJPatientPortalProfileManagementModel } from "@/Common/Models/Patient/UI/Profile/IPatientPortalProfileManagementModel";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { EmailAddressDto, MailingAddressDto, TelephoneNumberDto } from "@/Common/Models/Portal/MedfarPortal";
import { ContactType, GenderIdentity } from "@/Common/Models/Portal/MedfarPortalEnums";
import { PatientProfileMyleDto } from "@/Common/Models/Portal/Patient/SharedPatientMyleDto";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { IPortalLinkedItemModel } from "@/Common/Models/Portal/UI/IPortalLinkedItemModel";
import { QuestionnaireStatusInfo } from "@/Common/Models/Questionnaires/QuestionnaireStatusInfo";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";

class PatientHelper {
    private static canadaLookup: FlatLookupView;
    private static usaLookup: FlatLookupView;
    private static mexicoLookup: FlatLookupView;

    public static getRAMQCards(patient: Patient): PatientIdentifier[] {
        var quebecLookupCode = cacheStore.GetLookupIdForProvince(CanadaState.Quebec);
        return patient.HealthCards.filter((h: PatientIdentifier) => h.ProvinceLookupId === quebecLookupCode);
    }

    public static isDateOfBirthMatchingInPatientAndRAMQ(ramqNumber: string, birthDate: Date, isFemale: boolean): boolean {
        const ramqDOB = ramqNumber.slice(4).slice(0, 6);
        const momentDate = moment(birthDate).utcOffset(0, false);
        const year = momentDate.format("YY");
        const month = momentDate.format("MM");
        const day = momentDate.format("DD");

        return ramqDOB == `${year}${isFemale ? (+month + 50) : month}${day}`;
    }

    public static getDefaultPatientCreationModel(): IJPatientCreationModel {
        var maleLookup = cacheStore.FindLookupByLookupCode("MALE").LookupID;
        return {
            Id: "",
            FirstName: "",
            LastName: "",
            PreferredName: "",
            PreferredPronouns: PreferredPronouns.Unknown,
            SpecifiedPreferredPronouns: "",
            Prefix: "",
            GenderIdentity: GenderIdentity.Unknown,
            GenderLookupId: maleLookup,
            HealthCardNumber: "",
            HealthCardExpiryDate: null,
            FileNumber: "",
            Addresses: [],
            Emails: [],
            Identifier1: "",
            Identifier2: "",
            Identifier3: "",
            SelectedCountryLookupId: cacheStore.GetDefaultCountryForActiveSite()?.LookupID,
            SelectedRegionLookupId: cacheStore.GetDefaultRegionForActiveSite()?.LookupID,
            AvailableCountryLookupIds: cacheStore.GetCountryLookups(),
            AvailableRegionLookupIds: cacheStore.GetCountryRegionLookups(cacheStore.GetDefaultCountryForActiveSite()),
            CivicAddressEdit: "",
            CityEdit: "",
            PostalCodeEdit: "",
            NAMEdit: "",
            NAMExpiryEdit: null,
            selectedHealthCardRegionLookupId: cacheStore.GetDefaultRegionForActiveSite()?.LookupID,
            DOB: null,
            IsVIP: false,
            UpcomingAppointment: null,
            RelatedRecalls: [],
            FamilyDoctorId: "",
            IsFamilyDoctorFoundElsewhere: false,
            IsFamilyDoctorNotSubscribed: false,
            IsFamilyDoctorLoading: false,
            FamilyDoctorRAMQMessage: "",
            AvailablePrefixLookups: cacheStore.GetPrefixIndividualLookups(),
            SelectedPrefixLookupId: "",
            IsPostalCodeValid: true,
            IsNAMValid: true,
            IsPatientDuplicate: false,
            DuplictaeList: [],
            DuplicatePopupOpen: false,
            Phone1Edit: "",
            Phone1TypeLookupId: cacheStore.FindLookupByLookupCode("PHONE", "PHONETYPE").LookupID,
            Phone1ContactTypeLookupId: cacheStore.FindLookupByLookupCode("PERSONAL", "CONTACTPOINTTYPE").LookupID,
            Phone1Extension: "",
            IsPhone1Valid: true,
            Phone1NotificationsEnabled: false,
            Phone2Edit: "",
            Phone2TypeLookupId: cacheStore.FindLookupByLookupCode("MOBILE", "PHONETYPE").LookupID,
            Phone2ContactTypeLookupId: cacheStore.FindLookupByLookupCode("PERSONAL", "CONTACTPOINTTYPE").LookupID,
            Phone2Extension: "",
            IsPhone2Valid: true,
            Phone2NotificationsEnabled: false,
            EmailEdit: "",
            IsEmailValid: true,
            EmailTypeByLookup: cacheStore.FindLookupByLookupCode("PERSONAL", "CONTACTPOINTTYPE").LookupID,
            IsOkToContact: true,
            EmailNotificationsEnabled: false,

            PreferredSpokenLanguageLookupId: null,

            representantEnabled: false,
            representantNAMValid: true,
            representantRelationType: "RLMother",
            representantType: "MOP",
            representantNAM: "",
            representantFirstName: "",
            representantLastName: "",
            representantPersonSelected: null,
            motherLastName: "",
            motherFirstName: ""
        };
    }

    public static getDefaultPatientPortalManagementModel(): IJPatientPortalProfileManagementModel {
        return {
            BackendErrors: [],
            PatientProfileOverview: null
        };
    }

    public static getDateOfBirthFromNAM(val: string, namProvinceId: string): Date {
        if (namProvinceId == cacheStore.FindLookupByLookupCode("CanadaState_QC").LookupID && val != null && val.length == 12) {
            var currentYearDigits = Number(new Date().getFullYear().toString().substr(2, 2));
            var yearLastDigits = Number(val.substr(4, 2));

            if (yearLastDigits <= currentYearDigits) // ensure people born after 2000 do not end up with 1900s birthdate
                yearLastDigits = yearLastDigits + 2000;

            var monthsDigit = Number(val.substr(6, 2));
            if (monthsDigit > 50)
                monthsDigit = monthsDigit - 50;

            var days = Number(val.substr(8, 2));

            if (!isNaN(yearLastDigits) && !isNaN(monthsDigit) && monthsDigit > 0 && !isNaN(days) && days > 0)
                return new Date(yearLastDigits, monthsDigit - 1, days);
        }
        return null;
    }

    public static getFormattedAge(dob: Date, now: Date = null): string {
        var result = "";

        if (dob) {
            if (now == null) now = new Date();

            var yearNow = now.getFullYear();
            var monthNow = now.getMonth();
            var dateNow = now.getDate();
            var yearDob = dob.getFullYear();
            var monthDob = dob.getMonth();
            var dateDob = dob.getDate();

            var yearAge = yearNow - yearDob;

            if (monthNow >= monthDob)
                var monthAge = monthNow - monthDob;
            else {
                yearAge--;
                var monthAge = 12 + monthNow - monthDob;
            }

            if (dateNow < dateDob) {
                monthAge--;
                if (monthAge < 0) {
                    monthAge = 11;
                    yearAge--;
                }
            }

            result = yearAge + cacheStore.GetString("YearAbbrevLabel") + " " + monthAge + cacheStore.GetString("MonthAbbrevLabel");
        }
        return result;
    }

    public static getPatientCreationModelFromPortalPatient(portalPatient: PatientProfileMyleDto): IPatientCreationModel {
        if (this.canadaLookup == null) this.canadaLookup = cacheStore.FindLookupByLookupCode("CNCANADA");
        if (this.usaLookup == null) this.usaLookup = cacheStore.FindLookupByLookupCode("CNUSA");
        if (this.mexicoLookup == null) this.mexicoLookup = cacheStore.FindLookupByLookupCode("CNMEXICO");

        var newCreationModel = PatientHelper.getDefaultPatientCreationModel();
        newCreationModel.FirstName = portalPatient.FirstName;
        newCreationModel.LastName = portalPatient.LastName;
        newCreationModel.PreferredName = portalPatient.PreferredName;
        newCreationModel.GenderLookupId = PortalHelper.genderEnumToLookupId(portalPatient.Sex);
        newCreationModel.GenderIdentity = PortalHelper.convertPortalGenderIdentity(portalPatient.GenderIdentity);
        newCreationModel.PreferredPronouns = PortalHelper.convertPortalPreferredPronouns(portalPatient.PreferredPronouns);
        newCreationModel.SpecifiedPreferredPronouns = portalPatient.SpecifiedPreferredPronouns;
        newCreationModel.DOB = new Date(moment.utc(portalPatient.DateOfBirth).format("YYYY/MM/DD"));
        newCreationModel.SelectedPrefixLookupId = PortalHelper.getPrefixLookupIdFromGenderAndBirthDate(portalPatient.Sex, new Date(moment.utc(portalPatient.DateOfBirth).format("YYYY/MM/DD")));
        newCreationModel.NAMEdit = portalPatient.InsuranceNumber;

        let address: MailingAddressDto = ArrayHelper.Find(portalPatient.MailingAddresses, x => x.IsPrimary == true);
        if (address == null)
            address = ArrayHelper.FirstOrDefault(portalPatient.MailingAddresses);
        if (address != null) {
            const myleAddress = new Address({
                City: address.City,
                StreetAddress: address.Company
                    ? address.StreetAddress + " " + address.Company
                    : address.StreetAddress,
                ZipCode: address.PostCode
            });

            let countryLookup: FlatLookupView;
            let provinceLookup: FlatLookupView;

            if (address.CountryCode == "CA") {
                countryLookup = this.canadaLookup;
                let stateCode = "CanadaState_" + address.Province.toUpperCase();
                provinceLookup = cacheStore.FindLookupByLookupCode(stateCode);
            }
            else if (address.CountryCode == "US") {
                countryLookup = this.usaLookup;
                let stateCode = "USAState" + address.Province.toUpperCase();
                provinceLookup = cacheStore.FindLookupByLookupCode(stateCode);
            }
            else if (address.CountryCode == "MX") {
                countryLookup = this.usaLookup;
                let stateCode = "MEXICOSTATE_" + address.Province.toUpperCase();
                provinceLookup = cacheStore.FindLookupByLookupCode(stateCode);
            }

            if (countryLookup != null) {
                myleAddress.CountryLookupId = countryLookup.LookupID;
                myleAddress.Country = countryLookup.GetValue();
            }

            if (provinceLookup != null) {
                myleAddress.StateLookupId = provinceLookup.LookupID;
                myleAddress.State = provinceLookup.GetValue();
            }

            newCreationModel.CivicAddressEdit = myleAddress.StreetAddress;
            newCreationModel.Addresses = [myleAddress];
        }

        let sortedPhones = portalPatient.TelephoneNumbers.sortByProperty(item => item.IsPrimary);

        if (sortedPhones.length > 0) {
            let primaryPhone = sortedPhones[0];
            newCreationModel.Phone1Edit = primaryPhone.Number;
            newCreationModel.Phone1TypeLookupId = this.getPhoneTypeLookupId(primaryPhone);
            newCreationModel.Phone1ContactTypeLookupId = this.getContactPointTypeLookupId(primaryPhone);
        }

        if (sortedPhones.length > 1) {
            let secondaryPhone = sortedPhones[1];
            newCreationModel.Phone2Edit = secondaryPhone.Number;
            newCreationModel.Phone2TypeLookupId = this.getPhoneTypeLookupId(secondaryPhone);
            newCreationModel.Phone2ContactTypeLookupId = this.getContactPointTypeLookupId(secondaryPhone);
        }

        let email: EmailAddressDto = ArrayHelper.Find(portalPatient.EmailAddresses, x => x.IsPrimary == true);
        if (email == null)
            email = ArrayHelper.FirstOrDefault(portalPatient.EmailAddresses);
        if (email != null) {
            var myleEmail = new Email();
            myleEmail.Address = email.Email;
            myleEmail.IsOkToContact = true;
            newCreationModel.IsOkToContact = true;
            newCreationModel.EmailEdit = email.Email;
            if (email.ContactType == ContactType.Personal) {
                newCreationModel.EmailTypeByLookup = cacheStore.FindLookupByLookupCode("PERSONAL").LookupID;
            }
            else if (email.ContactType == ContactType.Work) {
                newCreationModel.EmailTypeByLookup = cacheStore.FindLookupByLookupCode("WORK").LookupID;
            }
        }

        newCreationModel.Emails = [myleEmail];

        let lang = "";
        switch (portalPatient.PreferredLanguage.toUpperCase()) {
            case "FR":
                lang = "FRENCH";
                break;
            case "ES":
                lang = "SPANISH";
                break;
            default:
                lang = "ENGLISH";
                break;
        }
        newCreationModel.PreferredSpokenLanguageLookupId = cacheStore.FindLookupByLookupCode(lang).LookupID;

        const result = Immutable.fromJS(newCreationModel);

        return result;
    }

    private static getPhoneTypeLookupId(phoneNumber: TelephoneNumberDto): string {
        return cacheStore.FindLookupByLookupCode(phoneNumber.PhoneTypeLookupCode, "PHONETYPE").LookupID;
    }

    private static getContactPointTypeLookupId(phoneNumber: TelephoneNumberDto): string {
        return cacheStore.FindLookupByLookupCode(phoneNumber.ContactPointTypeLookupCode, "CONTACTPOINTTYPE").LookupID;
    }

    public static handlePortalItemCheck(model: IPortalLinkedItemModel, checkedItem: SharedItemMessage): IPortalLinkedItemModel {
        let currentMessages = model.get("LinkedItemMessages").toArray();
        let msg = ArrayHelper.Find(currentMessages, (x: SharedItemMessage) => x.tmpIdForUI == checkedItem.tmpIdForUI);
        let idx = currentMessages.indexOf(msg);
        checkedItem.IsSelected = !checkedItem.IsSelected;
        currentMessages[idx] = checkedItem;
        model = model.set("LinkedItemMessages", Immutable.List(currentMessages));

        return model;
    }

    public static getShareItemMessagesToRevokeOrToSendToPortal(model: IPortalLinkedItemModel): SharedItemMessage[] {
        let linkedItemMsg = model.get("LinkedItemMessages").toArray();
        return linkedItemMsg.filter(x => {
            if (x.IsSelected && (x.Status === PortalSyncStatus.Revoked || x.Status === PortalSyncStatus.Unsent))
                return true;
            else if (!x.IsSelected && x.Status !== PortalSyncStatus.Revoked && x.Status !== PortalSyncStatus.Unsent)
                return true;

            return false;
        });
    }

    public static clearPortalLinkItemStates(model: IPortalLinkedItemModel): IPortalLinkedItemModel {
        let linkedItemMsg = model.get("LinkedItemMessages").toArray();
        linkedItemMsg.forEach(x => {
            x.IsSelected = false;
        });
        model = model.set("IsSending", false);
        model = model.set("LinkedItemMessages", linkedItemMsg.toImmutable());

        return model;
    }

    public static sortConsentPrograms(consentPrograms: PatientConsentModel[]): PatientConsentModel[] {
        return consentPrograms.sort((a, b) => {
            if (sessionStore.userLanguageCode == "fr") {
                if (a.NameFr > b.NameFr) return 1;
                if (a.NameFr < b.NameFr) return -1;
            }
            else if (sessionStore.userLanguageCode == "en") {
                if (a.NameEn > b.NameEn) return 1;
                if (a.NameEn < b.NameEn) return -1;
            }
            else if (sessionStore.userLanguageCode == "es-MX") {
                if (a.NameEs > b.NameEs) return 1;
                if (a.NameEs < b.NameEs) return -1;
            }

            if (a.ConsentDate && b.ConsentDate) {
                if (a.ConsentDate > b.ConsentDate) return 1;
                if (a.ConsentDate < b.ConsentDate) return -1;
            }

            return 0;
        });
    }

    public static getCostCenterId(): string {
        let costCenterId = null;
        let employeeUIModel = sessionStore.getCurrentEmployee();
        let currentEmployee = employeeUIModel ? employeeUIModel.Model : null;
        if (currentEmployee && currentEmployee.ActiveSiteId && currentEmployee.ActiveDepartmentId)
            costCenterId = currentEmployee.ActiveDepartmentId;

        return costCenterId;
    }

    public static async getPatientEmailValidator(patientContactId: Guid): Promise<PatientEmailValidator> {
        if (!patientContactId)
            throw new Error("getPatientEmailValidator() requires a valid patientContactId.");

        const representants = await RepresentantHelper.getPatientRepresentants(patientContactId);
        return new PatientEmailValidator(representants);
    }

    public static getAgeFromBirthday(birthday: DateTime): number {
        return moment().diff(moment(birthday), "years");
    }

    public static getPatientIsOfAgeOfConsent(dateOfBirth: DateTime): boolean {
        return PatientHelper.getAgeFromBirthday(dateOfBirth) >= cacheStore.ClinicSettings.PatientAgeOfConsent;
    }

    /**
     * Will check if patient has a legally valid email address, and invoke onNoValidEmail() if no valid address is found.
     *
     * @patientId patientId of patient for which check is performed.
     *
     * @onNoValidEmail method to call if no valid email is found.
    **/
    public static async ensurePatientHasValidEmail(patientId: Guid, onNoValidEmail: (patientEmailValidator: PatientEmailValidator) => Promise<void>): Promise<void> {
        if (!patientId)
            throw new Error("patientEmailValidator() requires a valid patientContactId.");

        const patientEmailValidator = await PatientHelper.getPatientEmailValidator(patientId);

        if (!patientEmailValidator.PatientHasValidEmail)
            await onNoValidEmail(patientEmailValidator);
    }

    public static isPatientChild(dateOfBirth: Date): boolean {
        const age = LuxonDateHelper.getYearsSince(dateOfBirth);
        return !IsNullOrUndefined(age)
            ? age < 18
            : false;
    }

    public static PrepareCreateNewPatientRequest(patientModel: IPatientCreationModel): Partial<CreateNewPatientRequest> {
        const requestData: Partial<CreateNewPatientRequest> = {
            SelectedPrefixLookupId: patientModel.get("SelectedPrefixLookupId"),
            FirstName: patientModel.get("FirstName"),
            LastName: patientModel.get("LastName"),
            PreferredName: patientModel.get("PreferredName"),
            GenderLookupId: patientModel.get("GenderLookupId"),
            DateOfBirth: patientModel.get("DOB"),
            GenderIdentity: patientModel.get("GenderIdentity"),
            PreferredPronouns: patientModel.get("PreferredPronouns"),
            SpecifiedPreferredPronouns: patientModel.get("SpecifiedPreferredPronouns"),
            CivicAddress: patientModel.get("CivicAddressEdit"),
            City: patientModel.get("CityEdit"),
            PostalCode: patientModel.get("PostalCodeEdit"),
            NAM: patientModel.get("NAMEdit"),
            NAMExpiry: patientModel.get("NAMExpiryEdit"),
            HealthCardRegionLookupId: IJPatientCreationModel.getHealthCardRegionLookupId(patientModel),

            Phone1Number: patientModel.get("Phone1Edit"),
            Phone1ContactTypeLookupId: patientModel.get("Phone1ContactTypeLookupId"),
            Phone1TypeLookupId: patientModel.get("Phone1TypeLookupId"),
            Phone1Ext: patientModel.get("Phone1Extension"),
            Phone1NotificationsEnabled: patientModel.get("Phone1NotificationsEnabled"),
            Phone2Number: patientModel.get("Phone2Edit"),
            Phone2ContactTypeLookupId: patientModel.get("Phone2ContactTypeLookupId"),
            Phone2TypeLookupId: patientModel.get("Phone2TypeLookupId"),
            Phone2Ext: patientModel.get("Phone2Extension"),
            Phone2NotificationsEnabled: patientModel.get("Phone2NotificationsEnabled"),
            EmailAddress: patientModel.get("EmailEdit"),
            EmailTypeByLookup: patientModel.get("EmailTypeByLookup"),
            IsOkToContact: patientModel.get("IsOkToContact"),
            EmailNotificationsEnabled: patientModel.get("EmailNotificationsEnabled"),

            CountryLookupId: patientModel.get("SelectedCountryLookupId"),
            RegionLookupId: patientModel.get("SelectedRegionLookupId"),
            MotherLastName: patientModel.get("motherLastName"),
            MotherFirstName: patientModel.get("motherFirstName")
        };

        if (patientModel.get("representantEnabled") == true) {
            requestData.representantRelationType = patientModel.get("representantRelationType");
            requestData.representantType = patientModel.get("representantType");

            if (patientModel.get("representantPersonSelected") != null) {
                requestData.representantContactId = patientModel.get("representantPersonSelected").ContactId;
            }
            else {
                requestData.representantNAM = patientModel.get("representantNAM");
                requestData.representantFirstName = patientModel.get("representantFirstName");
                requestData.representantLastName = patientModel.get("representantLastName");
            }
        }

        return requestData;
    }

    public static getAppointmentListFromResponse(appointmentList: any): PatientAppointmentListItem[] {
        return appointmentList.map(appointment => {
            const site = cacheStore.GetSiteById(appointment.SiteId);
            const dateStart = DateHelper.formatDate(appointment.StartDate, "dddd, MMMM DD, YYYY");
            const timeStart = DateHelper.formatDate(appointment.StartDate, "HH:mm:ss");
            const dateEnd = DateHelper.formatDate(appointment.EndDate, "dddd, MMMM DD, YYYY");
            const timeEnd = DateHelper.formatDate(appointment.EndDate, "HH:mm:ss");
            const itemModel = new PatientAppointmentItem({
                AppointmentId: appointment.AppointmentId,
                AppointmentComboId: appointment.AppointmentComboId,
                ServiceDefinitionId: appointment.ServiceDefinitionId,
                ServiceDefinitionName: appointment.ServiceDefinitionId && cacheStore.GetServiceDefinitionName(appointment.ServiceDefinitionId),
                ComboId: appointment.ComboId,
                ComboPrimaryAppointmentId: appointment.ComboPrimaryAppointmentId,
                IsSharedWithPortal: appointment.IsSharedWithPortal,
                Subject: appointment.Subject,
                Notes: appointment.Notes,
                SiteName: site?.Model.ClinicalName ?? "",
                SiteId: appointment.SiteId,
                StateId: appointment.StateId,
                CanadianTriageAndAcuityScore: appointment.CanadianTriageAndAcuityScore,
                IsFuturAppointment: appointment.IsFutureAppointment ?? false,
                With: "",
                IsPaid: appointment.IsPaid,
                IsPatientNotificationsEnabled: appointment.IsPatientNotificationsEnabled,
                IsTeleconsultation: appointment.IsTeleconsultation,
                QuestionnaireStatusInfo: appointment.QuestionnaireStatusInfo && new QuestionnaireStatusInfo(appointment.QuestionnaireStatusInfo),
                StartTimeFullDate: dateStart + " " + cacheStore.GetString("AtLabel") + " " + timeStart,
                StartTimeHour: DateHelper.formatDate(appointment.StartDate, "HH:mm"),
                StartTimeDateList: dateStart,
                StartDate: appointment.StartDate,
                EndTimeFullDate: dateEnd + " " + cacheStore.GetString("AtLabel") + " " + timeEnd,
                EndTimeHour: DateHelper.formatDate(appointment.EndDate, "HH:mm"),
                ComboName: appointment.ComboId && cacheStore.GetComboName(appointment.ComboId),
                StateName: cacheStore.GetLocalizedValueByLookupId(appointment.StateId),
                StateCode: cacheStore.GetLookupById(appointment.StateId)?.LookupCode,
                EmployeeAndRoomId: appointment.EmployeeAndRoomId?.toLowerCase(),
                Room: appointment.Room,
                IsReadOnly: appointment.IsReadOnly
            });

            if (appointment.EmployeeAndRoomId) {
                for (const employeeOrRoomId of appointment.EmployeeAndRoomId.split(";")) {
                    if (employeeOrRoomId) {
                        const employee = cacheStore.GetCliniciansPersonsByContactFacetId(employeeOrRoomId.toLowerCase());
                        if (employee) {
                            if (itemModel.With)
                                itemModel.With += ", ";
                            itemModel.With += employee.NameWithPrefix;
                        }
                    }
                }
            }

            return new PatientAppointmentListItem(itemModel);
        });
    }
}

interface CreateNewPatientRequest {
    SelectedPrefixLookupId: string;
    FirstName: string;
    LastName: string;
    PreferredName: string;
    GenderLookupId: string;
    DateOfBirth: Date;
    GenderIdentity: GenderIdentity;
    PreferredPronouns: PreferredPronouns;
    SpecifiedPreferredPronouns: string;
    CivicAddress: string;
    City: string;
    PostalCode: string;
    NAM: string;
    NAMExpiry: Date;
    HealthCardRegionLookupId: string;

    Phone1Number: string;
    Phone1ContactTypeLookupId: string;
    Phone1TypeLookupId: string;
    Phone1Ext: string;
    Phone1NotificationsEnabled: boolean;
    Phone2Number: string;
    Phone2ContactTypeLookupId: string;
    Phone2TypeLookupId: string;
    Phone2Ext: string;
    Phone2NotificationsEnabled: boolean;
    EmailAddress: string;
    EmailTypeByLookup: string;
    IsOkToContact: boolean;
    EmailNotificationsEnabled: boolean;

    CountryLookupId: string;
    RegionLookupId: string;
    MotherLastName: string;
    MotherFirstName: string;

    representantRelationType: string;
    representantType: string;
    representantContactId: string;
    representantNAM: string;
    representantFirstName: string;
    representantLastName: string;
}

export { CreateNewPatientRequest, PatientHelper };
